import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { licenseDateRange } from "../../../../shared/licenseDates";
import { userProductHardwareService } from "../../services/userProductHardwareService";
import { userProductLicenseService } from "../../services/userProductLicenseService";
import { isLicenseServerAssignment } from "../../utils/licenseServerUtils";
export default function HardwareIdSubmission({ products = [] }) {
  const [submissions, setSubmissions] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [hardwareId, setHardwareId] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const [licenseDevice, setLicenseDevice] = useState(null);
  const [expireDate, setExpireDate] = useState("");
  const expiryRange = licenseDateRange(
    licenseDevice?.product.UserProduct?.license_months ??
      licenseDevice?.product.user_products?.license_months ??
      1,
  );
  async function load() {
    const [hardware, licenseData] = await Promise.all([
      userProductHardwareService.getMyHardwareIds(),
      userProductLicenseService.getMyLicenses(),
    ]);
    setSubmissions(hardware.hardwareSubmissions || []);
    setLicenses(licenseData.productLicenses || []);
  }
  useEffect(() => {
    load()
      .catch((e) => setError(e.message || "Failed to load devices"))
      .finally(() => setLoading(false));
  }, []);
  async function run(action) {
    setBusy(true);
    setError("");
    try {
      await action();
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || "Request failed");
    } finally {
      setBusy(false);
    }
  }
  if (loading) return <CircularProgress />;
  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <Alert severity="info">
        Each device needs its own hardware ID and admin approval. Licenses use
        the type and maximum duration set by your administrator. Choose an
        expiry date when generating a license.
      </Alert>
      {products
        .filter((p) => p.requires_license && !isLicenseServerAssignment(p))
        .map((product) => {
          const devices = submissions.filter(
            (s) => s.product_id === product.id,
          );
          const limit =
            product.UserProduct?.license_limit ??
            product.user_products?.license_limit ??
            product.license_limit ??
            1;
          return (
            <Card key={product.id}>
              <CardContent>
                <Typography variant="h6">{product.name}</Typography>
                <Typography color="text.secondary">
                  {devices.length} / {limit} devices submitted
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  License Validity:{" "}
                  {product.UserProduct?.license_months ??
                    product.user_products?.license_months ??
                    1}{" "}
                  month(s) from generation
                </Typography>
                <Stack
                  spacing={2}
                  sx={{
                    my: 2,
                  }}
                >
                  {devices.map((device) => {
                    const license = licenses.find(
                      (l) =>
                        l.product_id === product.id &&
                        l.hardware_id === device.hardware_id,
                    );
                    const approved = device.is_confirmed && device.is_locked;
                    return (
                      <Box
                        key={device.id}
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          p: 2,
                          borderRadius: 1,
                        }}
                      >
                        <Typography>
                          {device.device_name || "Device"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            overflowWrap: "anywhere",
                          }}
                        >
                          {device.hardware_id}
                        </Typography>
                        <Chip
                          size="small"
                          label={approved ? "Approved" : "Pending approval"}
                          color={approved ? "success" : "warning"}
                          sx={{
                            my: 1,
                          }}
                        />
                        {license?.license_key && (
                          <>
                            <Typography variant="body2">
                              Expires:{" "}
                              {license.expiry_date
                                ? new Date(
                                    license.expiry_date,
                                  ).toLocaleDateString()
                                : "No expiry"}
                              {license.is_active === false ? " (inactive)" : ""}
                            </Typography>
                            <TextField
                              fullWidth
                              multiline
                              label="License key"
                              value={license.license_key}
                              slotProps={{
                                input: {
                                  readOnly: true,
                                },
                              }}
                              sx={{
                                my: 1,
                              }}
                            />
                            <Button
                              disabled={busy}
                              onClick={() =>
                                run(() =>
                                  navigator.clipboard.writeText(
                                    license.license_key,
                                  ),
                                )
                              }
                            >
                              Copy license
                            </Button>
                          </>
                        )}
                        {approved && !license?.license_key && (
                          <Button
                            disabled={busy}
                            onClick={() => {
                              setLicenseDevice({ product, device });
                              setExpireDate(
                                licenseDateRange(
                                  product.UserProduct?.license_months ??
                                    product.user_products?.license_months ??
                                    1,
                                ).max,
                              );
                            }}
                          >
                            Generate license
                          </Button>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
                <Button
                  variant="contained"
                  disabled={busy || devices.length >= limit}
                  onClick={() => {
                    setSelected(product);
                    setHardwareId("");
                    setDeviceName("");
                  }}
                >
                  Add hardware ID
                </Button>
              </CardContent>
            </Card>
          );
        })}
      <Dialog
        open={!!licenseDevice}
        onClose={() => !busy && setLicenseDevice(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Generate license for {licenseDevice?.product.name}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {licenseDevice?.device.device_name ||
              licenseDevice?.device.hardware_id}
          </Typography>
          <TextField
            fullWidth
            required
            label="Expiry date"
            type="date"
            margin="normal"
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: expiryRange.min, max: expiryRange.max }}
            helperText={`Select a date between ${expiryRange.min} and ${expiryRange.max}`}
            value={expireDate}
            onChange={(event) => setExpireDate(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setLicenseDevice(null)}>
            Cancel
          </Button>
          <Button
            disabled={
              busy ||
              !expireDate ||
              expireDate < expiryRange.min ||
              expireDate > expiryRange.max
            }
            onClick={() =>
              run(async () => {
                await userProductLicenseService.generateMyLicense(
                  licenseDevice.product.id,
                  licenseDevice.device.hardware_id,
                  expireDate,
                );
                setLicenseDevice(null);
              })
            }
          >
            Generate license
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={!!selected}
        onClose={() => !busy && setSelected(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Submit hardware ID for {selected?.name}</DialogTitle>
        <DialogContent>
          <Alert severity="info">
            Only an administrator can change a submitted hardware ID.
          </Alert>
          <TextField
            fullWidth
            required
            multiline
            label="Hardware ID"
            value={hardwareId}
            onChange={(e) => setHardwareId(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Device name (optional)"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            inputProps={{
              maxLength: 255,
            }}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setSelected(null)}>
            Cancel
          </Button>
          <Button
            disabled={busy || !hardwareId.trim()}
            onClick={() =>
              run(async () => {
                await userProductHardwareService.submitHardwareId(selected.id, {
                  hardwareId: hardwareId.trim(),
                  deviceName: deviceName.trim(),
                });
                setSelected(null);
              })
            }
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
